import { useCallback, useEffect, useRef, useState } from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Style } from 'ol/style';
import Icon from 'ol/style/Icon';
import { fetchPopulationInfo } from 'services/lab2/populationService';
import { buildPopulationSymbol } from 'utils/lab2/symbolBuilders';

const usePopulationLayer = ({
    mapRef,
    filter,
    selectedAge,
    mapLayerName,
    extent,
    isMapReady,
}) => {
    const pointLayerRef = useRef(null);
    const requestIdRef = useRef(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const ensurePointLayer = useCallback(() => {
        const map = mapRef.current;
        if (!map || !isMapReady) return null;

        if (pointLayerRef.current) {
            pointLayerRef.current.setZIndex(80);
            return pointLayerRef.current;
        }

        const existingLayer = map
            .getLayers()
            .getArray()
            .find((layer) => layer.get('name') === 'popLayer');

        if (existingLayer) {
            existingLayer.setZIndex(80);
            pointLayerRef.current = existingLayer;
            return existingLayer;
        }

        const pointLayer = new VectorLayer({
            name: 'popLayer',
            source: new VectorSource(),
        });
        pointLayer.setZIndex(80);
        map.addLayer(pointLayer);
        pointLayerRef.current = pointLayer;
        return pointLayer;
    }, [isMapReady, mapRef]);

    useEffect(() => {
        const map = mapRef.current;
        return () => {
            if (map && pointLayerRef.current) {
                map.removeLayer(pointLayerRef.current);
            }
            pointLayerRef.current = null;
        };
    }, [mapRef]);

    const refreshPopulationLayer = useCallback(async () => {
        const map = mapRef.current;
        const pointLayer = ensurePointLayer();
        if (!map || !pointLayer) {
            return;
        }

        const source = pointLayer.getSource();
        source.clear();

        const requestId = ++requestIdRef.current;

        setIsLoading(true);
        setError(null);

        try {
            const geojsonData = await fetchPopulationInfo({
                extent: extent ?? map.getView().calculateExtent(map.getSize()),
                age: selectedAge,
                layerName: mapLayerName,
            });

            if (requestIdRef.current !== requestId) {
                return;
            }

            const features = new GeoJSON().readFeatures(geojsonData, {
                featureProjection: 'EPSG:3857',
            });

            features.forEach((feature) => {
                feature.setStyle(
                    new Style({
                        image: new Icon({
                            src:
                                'data:image/svg+xml;utf8,' +
                                encodeURIComponent(buildPopulationSymbol(feature, filter)),
                            anchor: [0.5, 0.5],
                            scale: 1,
                        }),
                    })
                );
            });

            source.addFeatures(features);
        } catch (error) {
            console.error('Failed to refresh population layer', error);
            if (requestIdRef.current === requestId) {
                setError(error);
            }
        } finally {
            if (requestIdRef.current === requestId) {
                setIsLoading(false);
            }
        }
    }, [ensurePointLayer, extent, filter, mapLayerName, mapRef, selectedAge]);

    return { pointLayerRef, refreshPopulationLayer, isLoading, error };
};

export default usePopulationLayer;
